// @ts-check
/**
 * src/components/shared/modalSizes.js
 *
 * Size resolution and the guarded-close context for shared/Modal.jsx, kept in
 * their own module so Modal.jsx exports only the component.
 */
import { createContext, useContext } from 'react';

/**
 * Named widths (docs/ui-component-map-and-governance.md §3.4): `sm` confirm,
 * `md` form or picker, `lg` detail panel or wizard. Below 600 px every size
 * goes full-screen (globals.css), with the header and footer pinned.
 */
export const MODAL_SIZES = { sm: 480, md: 580, lg: 820 };

/**
 * Resolves the size a modal renders at. `maxWidth` is frozen debt from before
 * the named sizes and still wins so its call sites keep working; `wide` is the
 * old name for `lg`; no flag is `md`.
 *
 * @param {{ size?: string, wide?: boolean, maxWidth?: number | string }} props
 * @returns {{ size: 'sm' | 'md' | 'lg' | 'custom', maxWidth: number | string }}
 */
export function resolveModalSize({ size, wide, maxWidth }) {
  if (maxWidth) return { size: 'custom', maxWidth };
  const named = size && size in MODAL_SIZES ? size : wide ? 'lg' : 'md';
  return { size: /** @type {'sm'|'md'|'lg'} */ (named), maxWidth: MODAL_SIZES[named] };
}

/**
 * The modal's guarded close — the same unsaved-changes check ✕ and the
 * backdrop run — so a footer Cancel (ActionFooter) behaves identically.
 * `null` outside a Modal.
 * @type {import('react').Context<null | (() => void)>}
 */
export const ModalCloseContext = createContext(/** @type {null | (() => void)} */ (null));

/** @returns {null | (() => void)} */
export function useModalClose() {
  return useContext(ModalCloseContext);
}
